import React, { createContext, useContext } from 'react';
import ApiClient from '../../lib/api-client';
import { IClientCreationDTO } from '@common-frontend-submodule/interfaces/client/iclient-creation.interface';
import { IClientUpdateDTO } from '@common-frontend-submodule/interfaces/client/iclient-update.interface';
import { IGetClientsRequestDTO } from '@common-frontend-submodule/interfaces/client/iget-clients-request.interface';
import { IClientDTO } from '@common-frontend-submodule/interfaces/client/iclient.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { ClientRoutes } from '@common-frontend-submodule/enums/routes/client-routes.enum';
import { ClientFileRoutes } from '@common-frontend-submodule/enums/routes/client-file-routes.enum';
import { IClientShortDTO } from '@common-frontend-submodule/interfaces/client/iclient-short.interface';
import { IClientAllInfoDTO } from '@common-frontend-submodule/interfaces/client/iclient-all-info.interface';
import { IClientFileCreationDTO } from '@common-frontend-submodule/interfaces/client-file/iclient-file-creation.interface';
import { IClientFileDTO } from '@common-frontend-submodule/interfaces/client-file/iclient-file.interface';

const apiClient = ApiClient.getInstance();

interface ClientContextProps {
  getById: (clientId: string) => Promise<IApiResponseGenericDTO<IClientDTO>>;
  getAllInfoById: (clientId: string) => Promise<IApiResponseGenericDTO<IClientAllInfoDTO>>;
  all: () => Promise<IApiResponseGenericDTO<IClientShortDTO[]>>;
  filter: (model: IGetClientsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IClientDTO>>>;
  create: (model: IClientCreationDTO) => Promise<IApiResponseGenericDTO<IClientDTO>>;
  update: (clientId: string, model: IClientUpdateDTO) => Promise<IApiResponseGenericDTO<IClientDTO>>;
  delete: (clientId: string) => Promise<IApiResponseGenericDTO<boolean>>;
  generateExcel: () => Promise<void>;
  uploadFile: (file: File, model: IClientFileCreationDTO) => Promise<IApiResponseGenericDTO<boolean>>;
  getAllClientFiles: (clientId: string) => Promise<IApiResponseGenericDTO<IClientFileDTO[]>>;
}

const ClientContext = createContext<ClientContextProps | undefined>(undefined);

export const ClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getById = async (clientId: string) => {
    return await apiClient.getRequest<IClientDTO>(`${ClientRoutes.BasePrefix}/${clientId}`);
  };

  const getAllInfoById = async (clientId: string) => {
    return await apiClient.getRequest<IClientAllInfoDTO>(`${ClientRoutes.BasePrefix}/all-info/${clientId}`);
  };

  const all = async () => {
    return await apiClient.getRequest<IClientShortDTO[]>(`${ClientRoutes.BasePrefix}/${ClientRoutes.All}`);
  };

  const filter = async (model: IGetClientsRequestDTO) => {
    return await apiClient.postRequest<IGetClientsRequestDTO, IPaginatedResultDTO<IClientDTO>>(`${ClientRoutes.BasePrefix}/${ClientRoutes.List}`, model);
  };

  const create = async (model: IClientCreationDTO) => {
    return await apiClient.postRequest<IClientCreationDTO, IClientDTO>(ClientRoutes.BasePrefix, model);
  };

  const update = async (clientId: string, model: IClientUpdateDTO) => {
    return await apiClient.putRequest<IClientUpdateDTO, IClientDTO>(`${ClientRoutes.BasePrefix}/${clientId}`, model);
  };

  const deleteClient = async (clientId: string) => {
    return await apiClient.deleteRequest<null, boolean>(`${ClientRoutes.BasePrefix}/${clientId}`);
  };

  const generateExcel = async () => {
    return await apiClient.postBlob<null>(`${ClientRoutes.BasePrefix}/${ClientRoutes.GenerateExcel}`, {} as any, undefined, 'clients.xlsx');
  }

  const uploadFile = async (file: File, model: IClientFileCreationDTO): Promise<IApiResponseGenericDTO<boolean>> => {
    const formData = new FormData();

    formData.append('files', file);

    Object.entries(model).forEach(([key, value]) => {
      formData.append(key, value as string); // Преобразуем значение в строку
    });

    return await apiClient.postFormDataRequest<FormData, boolean>(
      ClientFileRoutes.BasePrefix,
      formData,
    );
  };

  const getAllClientFiles = async (clientId: string) => {
    return await apiClient.getRequest<IClientFileDTO[]>(`${ClientRoutes.BasePrefix}/files-all/${clientId}`);
  }


  return (
    <ClientContext.Provider value={{ getById, all, filter, create, update, delete: deleteClient, getAllInfoById, generateExcel, uploadFile, getAllClientFiles }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClientStore = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClientStore must be used within an ClientProvider');
  }
  return context;
};
